import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { Upload, FolderOpen, Image, Video, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const ClientUploadsPage = () => {
  const { api } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: 'brand_kit', label: 'Brand Kit', icon: FolderOpen },
    { id: 'raw_footage', label: 'Raw Footage', icon: Video },
    { id: 'testimonials', label: 'Testimonials', icon: FileText },
    { id: 'references', label: 'References', icon: Image },
  ];

  useEffect(() => {
    fetchUploads();
  }, []);

  const fetchUploads = async () => {
    try {
      const response = await api.get('/uploads');
      setUploads(response.data);
    } catch (error) {
      console.error('Failed to fetch uploads:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUploads = uploads.filter(
    upload => category === 'all' || upload.category === category
  );

  const getFileIcon = (category) => {
    const found = categories.find(c => c.id === category);
    return found?.icon || FileText;
  };

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="client-uploads">
        <div>
          <h1 className="text-2xl font-syne font-bold text-white">Brand Kit & Uploads</h1>
          <p className="text-fp-textMuted mt-1">Manage your brand assets and upload content</p>
        </div>

        {/* Categories */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id === category ? 'all' : cat.id)}
              className={`p-4 rounded-sm border transition-all ${
                category === cat.id
                  ? 'bg-primary/10 border-primary'
                  : 'bg-fp-surface border-white/5 hover:border-white/10'
              }`}
              data-testid={`category-${cat.id}`}
            >
              <cat.icon className={`w-6 h-6 mb-2 ${category === cat.id ? 'text-primary' : 'text-fp-textMuted'}`} />
              <p className={`text-sm font-medium ${category === cat.id ? 'text-white' : 'text-fp-textMuted'}`}>
                {cat.label}
              </p>
              <p className="text-xs text-fp-textMuted mt-1">
                {uploads.filter(u => u.category === cat.id).length} files
              </p>
            </button>
          ))}
        </div>

        {/* Upload Section */}
        <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
          <h2 className="font-syne font-bold text-white mb-4">Upload New Files</h2>
          <div className="border-2 border-dashed border-white/10 rounded-sm p-8 text-center hover:border-primary/30 transition-colors cursor-pointer">
            <Upload className="w-10 h-10 text-fp-textMuted mx-auto mb-4" />
            <p className="text-white mb-2">Drag & drop files here</p>
            <p className="text-fp-textMuted text-sm mb-4">or click to browse</p>
            <p className="text-fp-textMuted text-xs">
              Supported: Images, Videos, PDFs (Max 100MB)
            </p>
          </div>
        </div>

        {/* Files Grid */}
        <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-syne font-bold text-white">
              {category === 'all' ? 'All Files' : categories.find(c => c.id === category)?.label}
            </h2>
            <span className="text-fp-textMuted text-sm">{filteredUploads.length} files</span>
          </div>

          {filteredUploads.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 text-fp-textMuted mx-auto mb-4" />
              <p className="text-fp-textMuted">No files uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {filteredUploads.map((upload) => {
                const FileIcon = getFileIcon(upload.category);
                return (
                  <div
                    key={upload.id}
                    className="p-4 bg-fp-bg rounded-sm"
                    data-testid={`upload-${upload.id}`}
                  >
                    <div className="aspect-square bg-fp-surface rounded-sm flex items-center justify-center mb-3">
                      <FileIcon className="w-8 h-8 text-fp-textMuted" />
                    </div>
                    <p className="text-white text-sm font-medium truncate">{upload.name}</p>
                    <p className="text-fp-textMuted text-xs capitalize">{upload.category.replace('_', ' ')}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Brand Kit Checklist */}
        <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
          <h2 className="font-syne font-bold text-white mb-4">Brand Kit Checklist</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: 'Logo Files (PNG, SVG)', completed: true },
              { label: 'Brand Colors', completed: true },
              { label: 'Typography / Fonts', completed: false },
              { label: 'Brand Guidelines PDF', completed: false },
              { label: 'Product Photos', completed: true },
              { label: 'Team Photos', completed: false },
            ].map((item, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 p-3 rounded-sm ${
                  item.completed ? 'bg-green-500/10' : 'bg-fp-bg'
                }`}
              >
                <CheckCircle
                  className={`w-5 h-5 ${item.completed ? 'text-green-500' : 'text-fp-textMuted'}`}
                />
                <span className={item.completed ? 'text-white' : 'text-fp-textMuted'}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientUploadsPage;
